// Phenomizer Phase C — molecular support (pure). Grades how strongly a patient's
// pathogenic variant in a gene linked to a Profile Match disease supports that
// disease, weighted by the family's inheritance pattern for it (from the pedigree
// analyzer). Client-side re-rank/annotation only — no server scoring change.
//
// Strictly "molecular support", NEVER diagnosis: a heterozygous carrier of a
// recessive condition is carrier-level, not affected (plan §12.3).

import { PATTERN, CONFIDENCE } from './constants';

const DOMINANT = new Set([PATTERN.AD, PATTERN.XLD]);
const RECESSIVE = new Set([PATTERN.AR, PATTERN.XLR]);

// level: 'strong' | 'moderate' | 'carrier' | null. boost: multiplicative rank
// lift (0 = none). basis: why (for the UI tooltip).
export function molecularSupport({
  holdsPathogenicLinkedGene = false,
  inheritancePattern = null
} = {}) {
  if (!holdsPathogenicLinkedGene) return { level: null, boost: 0, basis: null };

  const id =
    inheritancePattern && !inheritancePattern.ruledOut
      ? inheritancePattern.id
      : null;
  const strong = inheritancePattern?.confidence === CONFIDENCE.strong;

  // Dominant predisposition: a single pathogenic allele is disease-causing —
  // strong molecular support (scaled by how confident the pattern is).
  if (id && DOMINANT.has(id))
    return {
      level: strong ? 'strong' : 'moderate',
      boost: strong ? 0.6 : 0.3,
      basis: 'dominant'
    };

  // Recessive: a heterozygous pathogenic variant is carrier-level only — flag,
  // do not meaningfully re-rank, and never imply the patient is affected.
  if (id && RECESSIVE.has(id))
    return { level: 'carrier', boost: 0.05, basis: 'recessive_carrier' };

  // Pathogenic linked gene but no usable family inheritance signal for this
  // disease — modest support from the gene link alone.
  return { level: 'moderate', boost: 0.25, basis: 'gene_link' };
}
